import React, {useRef} from 'react';
import {useForm, SubmitHandler} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {AuthActions, AuthEffects} from '../../store/auth';
import {RootState} from "../../store";
import {push} from "connected-react-router";
import {Routes} from "../../routes";
import ReCAPTCHA from 'react-google-recaptcha';
import {defaultMessageObj} from "../../util/constants";
import {ForgotPasswordFormData} from "../../util/interfaces";
import {
  CaptchaLegal,
  Form,
  FormCard,
  FormCardHeader,
  FormCardTransition,
  FormCardVariants,
  Line,
  LinkContainer,
  StyledA,
  StyledField,
  StyledLink,
  SubmitButton,
  CaptchaContainer
} from 'styles/styled';
import {TextInput} from "shared-react";
import MessageCard from "../../components/message-card";
import {motion} from "framer-motion";
import useDocumentTitle from "../../util/hooks/useDocumentTitle";

const schema = yup.object().shape({
  email: yup.string().email('A valid email is required').required('Email is a required field'),
});

const ForgotPassword = React.forwardRef(() => {

  useDocumentTitle('Forgot Password');
  const dispatch = useDispatch();
  const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
  const captchaKey = useSelector((state: RootState) => state.auth.captchaKey);
  const resetPasswordErrorMessage = useSelector((state: RootState) => state.auth.resetPasswordErrorMessage);
  const resetPasswordStatus = useSelector((state: RootState) => state.auth.resetPasswordStatus);
  const urlExtension = useSelector((state: RootState) => state.app.urlExtension);
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });
  let recaptchaRef = useRef<ReCAPTCHA>(null);

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = async (data: ForgotPasswordFormData) => {
    let token: string | null = null;
    if(!captchaDisabled && recaptchaRef.current){
      token = await recaptchaRef.current.executeAsync()
      recaptchaRef.current.reset();
    }
    dispatch(AuthEffects.startForgotPassword({...data, gCaptchaResponse: token}));
  };

  const handleReturnToLogin = () => {
    dispatch(AuthActions.clearResetPasswordStatus());
    dispatch(push(Routes.LOGIN + urlExtension));
  };

  let messageObj = {...defaultMessageObj};
  if(resetPasswordStatus === 'error') {
    messageObj = {
      message: resetPasswordErrorMessage || 'An unexpected error occurred.',
      type: 'warning'
    }
  } else if(resetPasswordStatus === 'initiated'){
    messageObj = {
      message: 'Email sent. If an account with that email address exists, you’ll receive an email immediately with a link to reset your password.',
      type: 'success'
    }
  }

  return (
    <motion.div
      initial="out"
      animate="in"
      exit="out"
      transition={FormCardTransition}
      variants={FormCardVariants}
    >
      <FormCard>
        <FormCardHeader>Forgot Password</FormCardHeader>
        <Line/>
        <MessageCard type={messageObj.type}>
          {messageObj.message}
        </MessageCard>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <StyledField errorMessage={errors.email?.message} error={errors.email} label="Email Address">
            <TextInput aria-label="Email" type="email" {...register('email')}/>
          </StyledField>
          <CaptchaContainer>
            {!captchaDisabled &&
            <ReCAPTCHA
                sitekey={captchaKey}
                size="invisible"
                badge="inline"
                ref={recaptchaRef}
            />}
          </CaptchaContainer>
          <SubmitButton type="submit" aria-label="submit">Reset Password</SubmitButton>
          {!captchaDisabled && <CaptchaLegal>
            This site is protected by reCAPTCHA and the Google
            <StyledA
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer noopener"
            > Privacy Policy </StyledA>
            and
            <StyledA
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer noopener"
            > Terms of Service </StyledA>
            apply.
          </CaptchaLegal>}
        </Form>
      </FormCard>
      <LinkContainer>
        <StyledLink as="a" aria-label="return to login" onClick={handleReturnToLogin}>Return to login</StyledLink>
      </LinkContainer>
    </motion.div>

  );
});

export default ForgotPassword;
